import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  ArrowLeft, Award, CheckCircle, XCircle, HelpCircle,
  Loader2, RefreshCw, Download, ChevronRight, AlertTriangle,
  Share2, Linkedin, Twitter
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AcademyFinalQuiz = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login?redirect=/academy/course/' + slug + '/final-quiz');
      return;
    }
    fetchQuiz();
  }, [slug, user]);

  const fetchQuiz = async () => {
    try {
      // First get course by slug to get ID
      const courseRes = await axios.get(`${API}/academy/course/${slug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const courseId = courseRes.data.id;

      const res = await axios.get(`${API}/academy/course/${courseId}/final-quiz`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Membership required');
        navigate('/academy/membership');
      } else {
        toast.error('Failed to load quiz');
        navigate(`/academy/course/${slug}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!data?.quiz?.questions) return;
    const qIds = data.quiz.questions.map(q => q.id);
    if (!qIds.every(id => answers[id] !== undefined)) {
      toast.error('Please answer all questions');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/academy/course/${data.course.id}/final-quiz/submit`,
        { answers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResults(res.data);
      if (res.data.passed) {
        toast.success('Congratulations! You passed the final quiz!');
      } else {
        toast.error('Not quite. Review the material and try again.');
      }
    } catch (error) {
      toast.error('Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const resetQuiz = () => {
    setAnswers({});
    setResults(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { quiz, course, all_lessons_complete, previous_passed, certificate } = data;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="academy-final-quiz">
      <Navbar />

      <div className="pt-16 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button
            variant="ghost"
            className="text-slate-400 hover:text-white mb-4 -ml-4"
            onClick={() => navigate(`/academy/course/${slug}`)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to {course.title}
          </Button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-primary/20 rounded-xl flex items-center justify-center">
              <Award className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold">Final Assessment</h1>
              <p className="text-slate-400">{course.title}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Already passed */}
        {previous_passed && !results && (
          <Card className="mb-6 bg-green-50 border-green-200">
            <CardContent className="p-6 flex items-center gap-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
              <div className="flex-1">
                <h3 className="font-semibold text-green-900">You've already passed this quiz!</h3>
                <p className="text-sm text-green-700">Your certificate is ready for download.</p>
              </div>
              {certificate && (
                <div className="flex items-center gap-2">
                  <a
                    href={`${API}/academy/certificate/${certificate.id}/download`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button data-testid="download-cert-btn">
                      <Download className="w-4 h-4 mr-2" />
                      Download Certificate
                    </Button>
                  </a>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const text = `I just earned my "${course.title}" certificate on AISkillFlow! 🎓`;
                      const url = window.location.origin;
                      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(text)}`, '_blank', 'width=600,height=400');
                    }}
                    title="Share on LinkedIn"
                    data-testid="share-linkedin-btn"
                  >
                    <Linkedin className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const text = `I just earned my "${course.title}" certificate on AISkillFlow! 🎓 #AI #Learning`;
                      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank', 'width=600,height=400');
                    }}
                    title="Share on X/Twitter"
                    data-testid="share-twitter-btn"
                  >
                    <Twitter className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Not all lessons complete */}
        {!all_lessons_complete && !previous_passed && (
          <Card className="mb-6 bg-yellow-50 border-yellow-200">
            <CardContent className="p-6 flex items-center gap-4">
              <AlertTriangle className="w-8 h-8 text-yellow-600" />
              <div>
                <h3 className="font-semibold text-yellow-900">Complete all lessons first</h3>
                <p className="text-sm text-yellow-700">
                  You need to pass all lesson quizzes before taking the final assessment.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {results ? (
          <div className="space-y-6">
            <Card>
              <CardContent className="p-8">
                <div className={`text-center ${results.passed ? 'text-green-600' : 'text-red-600'}`}>
                  <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${
                    results.passed ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {results.passed ? (
                      <CheckCircle className="w-10 h-10" />
                    ) : (
                      <XCircle className="w-10 h-10" />
                    )}
                  </div>
                  <h2 className="text-2xl font-bold mb-2">
                    {results.passed ? 'Congratulations!' : 'Not Quite'}
                  </h2>
                  <p className="text-4xl font-bold mb-2">{Math.round(results.score)}%</p>
                  <p className="text-slate-600">
                    {results.correct_count} of {results.total_questions} correct
                  </p>
                </div>
              </CardContent>
            </Card>

            {results.results.map((r, idx) => {
              const q = quiz.questions.find(q => q.id === r.question_id);
              return (
                <Card key={r.question_id} className={r.is_correct ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      {r.is_correct ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                      )}
                      <div>
                        <p className="font-medium mb-1">{q?.question}</p>
                        {!r.is_correct && (
                          <p className="text-sm text-green-700">Correct: {q?.options[r.correct_answer]}</p>
                        )}
                        <p className="text-sm text-slate-600 mt-1">{r.explanation}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            <div className="flex gap-3 justify-center">
              {!results.passed && (
                <Button onClick={resetQuiz} variant="outline">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
              )}
              {results.passed && (
                <>
                  <Button onClick={() => navigate('/academy/dashboard')}>
                    Go to Dashboard
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const text = `I just earned my "${course.title}" certificate on AISkillFlow! 🎓`;
                      const url = window.location.origin;
                      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(text)}`, '_blank', 'width=600,height=400');
                    }}
                    data-testid="share-linkedin-results-btn"
                  >
                    <Linkedin className="w-4 h-4 mr-2" />
                    Share on LinkedIn
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const text = `I just earned my "${course.title}" certificate on AISkillFlow! 🎓 #AI #Learning`;
                      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank', 'width=600,height=400');
                    }}
                    data-testid="share-twitter-results-btn"
                  >
                    <Twitter className="w-4 h-4 mr-2" />
                    Share on X
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : (
          /* Quiz Form */
          <Card>
            <CardHeader className="bg-primary/5 border-b border-primary/20">
              <div className="flex items-center gap-3">
                <HelpCircle className="w-6 h-6 text-primary" />
                <div>
                  <CardTitle>{quiz.title}</CardTitle>
                  <p className="text-sm text-slate-600 mt-1">
                    {quiz.questions?.length} questions · Pass with 70% to earn your certificate
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {(!all_lessons_complete && !previous_passed) ? (
                <div className="text-center py-8">
                  <p className="text-slate-600 mb-4">Complete all lesson quizzes to unlock this assessment.</p>
                  <Button onClick={() => navigate(`/academy/course/${slug}`)}>
                    Back to Course
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center justify-between text-sm text-slate-600">
                    <span>{Object.keys(answers).length} of {quiz.questions?.length} answered</span>
                    <Progress
                      value={(Object.keys(answers).length / (quiz.questions?.length || 1)) * 100}
                      className="w-32 h-2"
                    />
                  </div>

                  {quiz.questions?.map((q, idx) => (
                    <div key={q.id} className="p-4 bg-slate-50 rounded-lg">
                      <p className="font-medium text-slate-900 mb-4">{idx + 1}. {q.question}</p>
                      <RadioGroup
                        value={answers[q.id]?.toString()}
                        onValueChange={(v) => setAnswers({ ...answers, [q.id]: parseInt(v) })}
                      >
                        {q.options?.map((opt, optIdx) => (
                          <div key={optIdx} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white transition-colors">
                            <RadioGroupItem value={optIdx.toString()} id={`fq-${q.id}-${optIdx}`} />
                            <Label htmlFor={`fq-${q.id}-${optIdx}`} className="flex-1 cursor-pointer">{opt}</Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                  ))}

                  <Button
                    className="w-full"
                    onClick={handleSubmit}
                    disabled={submitting}
                    data-testid="submit-final-quiz-btn"
                  >
                    {submitting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                    ) : (
                      'Submit Final Quiz'
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AcademyFinalQuiz;
